import { useEffect, useState } from 'react';
import { AdminOfferDetail } from '../../types/offers';
import { formatAed, misleadingClaimHits } from './offerReviewModel';

type HintLevel = 'ok' | 'warn' | 'check';

interface PolicyItem {
  id: string;
  rule: string;
  hint: string;
  level: HintLevel;
}

interface ImageSize {
  width: number;
  height: number;
}

/** Natural size of the banner, for the P3 hint. Null while loading or when it fails. */
function useImageSize(url: string | null | undefined): ImageSize | null {
  const [size, setSize] = useState<ImageSize | null>(null);
  useEffect(() => {
    setSize(null);
    if (!url) return;
    let cancelled = false;
    const img = new Image();
    img.onload = () => { if (!cancelled) setSize({ width: img.naturalWidth, height: img.naturalHeight }); };
    img.src = url;
    return () => { cancelled = true; };
  }, [url]);
  return size;
}

/** P3: legible at 2:1 on a 400 px wide phone → about 2:1 and at least 800 px wide (2x density). */
function imageHint(hasBanner: boolean, size: ImageSize | null): Pick<PolicyItem, 'hint' | 'level'> {
  if (!hasBanner) return { hint: 'No banner uploaded.', level: 'warn' };
  if (!size) return { hint: 'Check the preview at phone width.', level: 'check' };
  const ratio = size.width / size.height;
  if (Math.abs(ratio - 2) > 0.1) return { hint: `Banner is ${size.width}×${size.height}, not 2:1.`, level: 'warn' };
  if (size.width < 800) return { hint: `Banner is only ${size.width}px wide; it may look soft.`, level: 'warn' };
  return { hint: `Banner is ${size.width}×${size.height} (2:1). Check the text is readable in the preview.`, level: 'check' };
}

function buildPolicyItems(detail: AdminOfferDetail, size: ImageSize | null): PolicyItem[] {
  const { offer } = detail;
  const claims = misleadingClaimHits(offer);
  const prices = offer.items
    .slice(0, 3)
    .map((i) => `${formatAed(i.listPrice)} → ${formatAed(i.discountedPrice)}`)
    .join(', ');

  const items: PolicyItem[] = [
    {
      id: 'P1',
      rule: 'No phone, WhatsApp, email, URL or social handle on the banner or in the text',
      ...(detail.contactDetailsInText
        ? { hint: 'A contact pattern was found in the offer text.', level: 'warn' as const }
        : { hint: 'No contact pattern in the text. Check the banner image.', level: 'check' as const }),
    },
    {
      id: 'P2',
      rule: 'Any price on the banner equals the computed offer price',
      hint: prices ? `Computed: ${prices}${offer.items.length > 3 ? ', …' : ''}.` : 'No covered services.',
      level: prices ? 'check' : 'warn',
    },
    { id: 'P3', rule: 'Image is legible at 2:1 on a 400 px wide phone', ...imageHint(!!offer.banner?.downloadUrl, size) },
    {
      id: 'P4',
      rule: 'No misleading claims ("free", "guaranteed") unless the offer delivers them',
      ...(claims.length
        ? { hint: `Text uses: ${claims.map((c) => `"${c}"`).join(', ')}.`, level: 'warn' as const }
        : { hint: 'No claim words in the text. Check the banner image.', level: 'check' as const }),
    },
    {
      id: 'P5',
      rule: 'No third-party or competitor branding',
      hint: 'Check the banner for other brands\' logos or names.',
      level: 'check',
    },
  ];

  items.push({
    id: 'AR',
    rule: 'Arabic title present',
    ...(detail.arabicMissing
      ? { hint: 'Arabic is missing: Arabic users see the English title.', level: 'warn' as const }
      : { hint: 'Arabic title provided.', level: 'ok' as const }),
  });
  return items;
}

const LEVEL_TEXT: Record<HintLevel, string> = { ok: 'OK', warn: 'Flag', check: 'Check' };

/**
 * Spec 062 content policy P1–P5 (requirements §3) with hints computed from the detail. The ticks are a
 * rubric for the reviewer; they are not sent and do not gate the decision.
 */
export default function PolicyChecklist({ detail }: { detail: AdminOfferDetail }) {
  const size = useImageSize(detail.offer.banner?.downloadUrl);
  const [ticked, setTicked] = useState<Record<string, boolean>>({});
  const items = buildPolicyItems(detail, size);

  return (
    <div className="admin-card offer-policy" data-testid="offer-policy">
      <div className="admin-card-header">
        <h3 className="admin-card-title">Policy checklist</h3>
      </div>
      <ul className="offer-policy__list">
        {items.map((item) => (
          <li key={item.id} className={`offer-policy__item offer-policy__item--${item.level}`} data-testid={`policy-${item.id}`}>
            <label className="offer-policy__label">
              <input
                type="checkbox"
                checked={!!ticked[item.id]}
                onChange={(e) => setTicked((t) => ({ ...t, [item.id]: e.target.checked }))}
              />
              <span className="offer-policy__id">{item.id}</span>
              <span className="offer-policy__rule">{item.rule}</span>
            </label>
            <div className="offer-policy__hint">
              <span className={`offer-policy__level offer-policy__level--${item.level}`}>{LEVEL_TEXT[item.level]}</span>
              {item.hint}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
